/**
 * Build a short Paladin-branded animated PR video (MP4 preferred, WebM fallback)
 * at 1080x1920 (Instagram Stories / TikTok vertical).
 *
 * Returns: { blob: Blob, mime: string, extension: "mp4" | "webm" } | null
 *
 * Animation timeline (2 seconds @ 30fps):
 *   0.0-0.3s  confetti particles fall into frame
 *   0.2-1.2s  PR weight counts up 0 → final
 *   1.2-1.5s  fun comparison scales in
 *   1.5-2.0s  hold
 */

const W = 1080;
const H = 1920;
const FPS = 30;
const DURATION_MS = 2000;

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

function easeOut(t) {
  return 1 - Math.pow(1 - t, 3);
}

function pickRecorderMime() {
  if (typeof MediaRecorder === "undefined") return null;
  const candidates = [
    { mime: "video/mp4;codecs=avc1", extension: "mp4" },
    { mime: "video/mp4", extension: "mp4" },
    { mime: "video/webm;codecs=vp9", extension: "webm" },
    { mime: "video/webm;codecs=vp8", extension: "webm" },
    { mime: "video/webm", extension: "webm" },
  ];
  for (const c of candidates) {
    if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(c.mime)) {
      return c;
    }
  }
  return null;
}

/**
 * data: {
 *   athleteName: string,
 *   volume: number,
 *   sets: number,
 *   duration: number,
 *   pr?: { exercise, weight, previous } | null,
 *   funLine?: string
 * }
 */
export async function buildShareVideoBlob(data) {
  const recorderChoice = pickRecorderMime();
  if (!recorderChoice) return null; // Caller should fall back to static image

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Capture stream for MediaRecorder
  const stream = canvas.captureStream(FPS);
  let recorder;
  try {
    recorder = new MediaRecorder(stream, {
      mimeType: recorderChoice.mime,
      videoBitsPerSecond: 4_000_000,
    });
  } catch {
    return null;
  }

  const chunks = [];
  recorder.ondataavailable = (e) => {
    if (e.data && e.data.size > 0) chunks.push(e.data);
  };

  const donePromise = new Promise((resolve, reject) => {
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: recorderChoice.mime });
      resolve({ blob, mime: recorderChoice.mime, extension: recorderChoice.extension });
    };
    recorder.onerror = reject;
  });

  recorder.start();

  // Pre-generate deterministic confetti particles
  const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899"];
  const particles = Array.from({ length: 60 }, (_, i) => ({
    x: Math.random() * W,
    xDrift: (Math.random() - 0.5) * 0.4 * W,
    size: 14 + Math.random() * 18,
    color: COLORS[i % COLORS.length],
    startDelay: Math.random() * 400, // ms
    fallDuration: 900 + Math.random() * 700,
    rotStart: Math.random() * Math.PI * 2,
    rotSpeed: (Math.random() - 0.5) * 0.2,
    shape: Math.random() > 0.5 ? "square" : "circle",
  }));

  const hasPR = !!data.pr;
  const targetWeight = hasPR ? Math.round(data.pr.weight) : 0;
  const prevWeight = hasPR ? Math.round(data.pr.previous || 0) : 0;

  const drawFrame = (tMs) => {
    // Background gradient
    const bg = ctx.createLinearGradient(0, 0, 0, H);
    bg.addColorStop(0, "#0B0F1A");
    bg.addColorStop(0.5, "#0F172A");
    bg.addColorStop(1, "#1E293B");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    // Radial glow (intensity pulses slightly)
    const pulse = 0.3 + 0.1 * Math.sin((tMs / 1000) * Math.PI);
    const glow = ctx.createRadialGradient(W / 2, H * 0.3, 10, W / 2, H * 0.3, 950);
    glow.addColorStop(0, `rgba(59, 130, 246, ${pulse})`);
    glow.addColorStop(1, "rgba(59, 130, 246, 0)");
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, W, H);

    // Paladin wordmark
    ctx.fillStyle = "#3B82F6";
    ctx.font = "900 72px system-ui, -apple-system, Arial";
    ctx.textAlign = "center";
    ctx.fillText("PALADIN", W / 2, 200);
    ctx.fillStyle = "#9CA3AF";
    ctx.font = "600 24px system-ui, -apple-system, Arial";
    ctx.fillText("HEALTH · HUMAN PERFORMANCE", W / 2, 246);

    // Main card
    const cardX = 80;
    const cardY = 360;
    const cardW = W - 160;
    const cardH = hasPR ? 1000 : 860;
    ctx.fillStyle = "rgba(20, 27, 45, 0.85)";
    roundRect(ctx, cardX, cardY, cardW, cardH, 40);
    ctx.fill();
    ctx.strokeStyle = "rgba(59, 130, 246, 0.35)";
    ctx.lineWidth = 3;
    roundRect(ctx, cardX, cardY, cardW, cardH, 40);
    ctx.stroke();

    let y = cardY + 110;

    if (hasPR) {
      ctx.fillStyle = "#F59E0B";
      ctx.font = "700 30px system-ui, -apple-system, Arial";
      ctx.textAlign = "center";
      ctx.fillText("NEW PERSONAL RECORD", W / 2, y);
      y += 60;

      ctx.fillStyle = "#FFFFFF";
      ctx.font = "800 52px system-ui, -apple-system, Arial";
      ctx.fillText(data.pr.exercise || "Exercise", W / 2, y);
      y += 100;

      // Count-up animation: 0.2s → 1.2s (1000ms duration)
      const countStart = 200;
      const countEnd = 1200;
      let countT = (tMs - countStart) / (countEnd - countStart);
      countT = Math.max(0, Math.min(1, countT));
      const displayedWeight = Math.round(easeOut(countT) * targetWeight);

      // Big weight number
      ctx.fillStyle = "#3B82F6";
      ctx.font = "900 220px system-ui, -apple-system, Arial";
      ctx.fillText(`${displayedWeight}`, W / 2, y + 150);
      ctx.fillStyle = "#FFFFFF";
      ctx.font = "700 52px system-ui, -apple-system, Arial";
      ctx.fillText("lbs", W / 2, y + 220);
      y += 310;

      // +delta (appears at 1200ms)
      if (prevWeight > 0 && tMs > 1150) {
        const deltaT = Math.min(1, (tMs - 1150) / 250);
        ctx.globalAlpha = deltaT;
        ctx.fillStyle = "#10B981";
        ctx.font = "800 44px system-ui, -apple-system, Arial";
        ctx.fillText(
          `+${targetWeight - prevWeight} lbs over previous best`,
          W / 2,
          y
        );
        ctx.globalAlpha = 1;
      }
    } else {
      ctx.fillStyle = "#10B981";
      ctx.font = "700 30px system-ui, -apple-system, Arial";
      ctx.textAlign = "center";
      ctx.fillText("WORKOUT COMPLETE", W / 2, y);
      y += 80;
    }

    // Stats row (fades in at 1.0s)
    const statsOpacity = Math.max(0, Math.min(1, (tMs - 900) / 400));
    if (statsOpacity > 0) {
      const statY = cardY + cardH - 260;
      const statsData = [
        { v: `${Math.round(data.volume || 0).toLocaleString()}`, l: "LBS LIFTED" },
        { v: `${data.sets || 0}`, l: "SETS" },
        { v: `${data.duration || 0}m`, l: "DURATION" },
      ];
      const colW = cardW / statsData.length;
      ctx.globalAlpha = statsOpacity;
      statsData.forEach((s, i) => {
        const cx = cardX + colW * i + colW / 2;
        ctx.fillStyle = "#FFFFFF";
        ctx.font = "800 56px system-ui, -apple-system, Arial";
        ctx.fillText(s.v, cx, statY + 60);
        ctx.fillStyle = "#6B7280";
        ctx.font = "700 24px system-ui, -apple-system, Arial";
        ctx.fillText(s.l, cx, statY + 100);
      });
      ctx.globalAlpha = 1;
    }

    // Fun line pill (scales in at 1.2s)
    if (data.funLine && tMs > 1150) {
      const fT = Math.min(1, (tMs - 1150) / 350);
      const scale = 0.7 + 0.3 * easeOut(fT);
      const fy = cardY + cardH - 80;
      const funBoxW = cardW - 80;

      ctx.save();
      ctx.translate(W / 2, fy);
      ctx.scale(scale, scale);
      ctx.globalAlpha = fT;

      ctx.fillStyle = "rgba(245, 158, 11, 0.15)";
      roundRect(ctx, -funBoxW / 2, -42, funBoxW, 78, 40);
      ctx.fill();
      ctx.fillStyle = "#F59E0B";
      ctx.font = "700 32px system-ui, -apple-system, Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(data.funLine, 0, 0);
      ctx.textBaseline = "alphabetic";
      ctx.globalAlpha = 1;
      ctx.restore();
    }

    // Footer
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "700 40px system-ui, -apple-system, Arial";
    ctx.textAlign = "center";
    ctx.fillText(data.athleteName || "", W / 2, H - 220);
    ctx.fillStyle = "#9CA3AF";
    ctx.font = "600 28px system-ui, -apple-system, Arial";
    ctx.fillText("paladinhhp.com", W / 2, H - 170);

    // Confetti particles (drawn on top)
    particles.forEach((p) => {
      const pT = (tMs - p.startDelay) / p.fallDuration;
      if (pT < 0 || pT > 1) return;
      const py = easeOut(pT) * (H + 100);
      const px = p.x + p.xDrift * pT;
      const rot = p.rotStart + p.rotSpeed * tMs;
      const opacity = pT < 0.9 ? 1 : 1 - (pT - 0.9) / 0.1;

      ctx.save();
      ctx.globalAlpha = opacity;
      ctx.translate(px, py);
      ctx.rotate(rot);
      ctx.fillStyle = p.color;
      if (p.shape === "circle") {
        ctx.beginPath();
        ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
      }
      ctx.restore();
    });
  };

  // Animation loop driven by timestamp — we don't use requestAnimationFrame
  // because background tabs throttle it. Use setTimeout at frame interval.
  const frameInterval = 1000 / FPS;
  const totalFrames = Math.round(DURATION_MS / frameInterval);

  for (let i = 0; i < totalFrames; i++) {
    const tMs = i * frameInterval;
    drawFrame(tMs);
    // Yield a frame so MediaRecorder can capture
    await new Promise((r) => setTimeout(r, frameInterval));
  }

  // Hold last frame for 100ms extra so the final state is captured cleanly
  await new Promise((r) => setTimeout(r, 100));

  recorder.stop();
  return donePromise;
}
